import { animate, style, transition, trigger } from '@angular/animations';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
} from '@angular/core';
import {
  ApiService,
  BackpackerRef,
  GoogleReview,
  GoogleReviewsResponse,
  Hostel,
  Review
} from '@app/services/api/api.service';
import { Subject } from 'rxjs';

@Component({
  selector: 'app-reviews',
  templateUrl: './reviews.component.html',
  styleUrls: ['./reviews.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('fadeIn', [
      transition(':enter', [
        style({ transform: 'translateY(-8px)', opacity: 0.4 }),
        animate(
          '0.15s ease-in',
          style({ transform: 'translateY(0)', opacity: 1 })
        ),
      ]),
    ]),
  ],
})
export class ReviewsComponent {
  @Input() public hostel: Hostel; // Add googleReviewData input to get data about Google reviews

  private destroyed = new Subject<void>();
  public top4Reviews: any[] = [];
  public useGoogleReviews: boolean = false;

  constructor(
    private readonly apiService: ApiService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnChanges() {
    if (this.hostel?.reviews?.length < 4) {
      this.useGoogleReviews = true;

      if (
        this.hostel.google_review_data &&
        this.hostel.google_review_data.reviews_count > 0
      ) {
        this.fetchGoogleReviews(this.hostel._id);
      } else {
        this.top4Reviews = this.hostel?.reviews ?? [];
        this.cdr.markForCheck(); // Manually trigger change detection
      }
    } else {
      this.useGoogleReviews = false;
      this.top4Reviews = this.hostel?.reviews?.slice(0, 4) ?? [];
      this.cdr.markForCheck(); // Manually trigger change detection
    }
  }

  private fetchGoogleReviews(hostelId: string) {
    this.apiService.getGoogleReviews(hostelId).subscribe({
      next: (response: GoogleReviewsResponse) => {
        if (response.success && response.data && response.data.reviews_data) {
          const googleReviews = response.data.reviews_data;

          if (googleReviews.length > 0) {
            const mappedReviews = googleReviews
              .slice(0, 4)
              .map(this.convertGoogleReviewToReview);
            this.top4Reviews = mappedReviews;
            this.cdr.markForCheck(); // Manually trigger change detection
          } else {
            this.top4Reviews = this.hostel?.reviews ?? [];
            this.cdr.markForCheck();
          }
        } else {
          this.top4Reviews = this.hostel?.reviews ?? [];
          this.cdr.markForCheck();
        }
      },
      error: (err) => {
        console.error('Failed to fetch Google reviews', err);
        this.top4Reviews = this.hostel?.reviews ?? [];
        this.cdr.markForCheck();
      },
    });
  }

  private convertGoogleReviewToReview(googleReview: GoogleReview): Review {
    const backpacker: BackpackerRef = {
      _id: googleReview.author_id,
      nationality: '', // Leave empty if not available
      username: googleReview.author_title,
      profilePicture: googleReview.author_image,
      email: undefined, // Not provided in GoogleReview
    };

    return {
      status: 1, // Assuming 1 means 'active'
      badges: [], // Assuming no badges are part of GoogleReview
      _id: googleReview.review_id,
      property: googleReview.google_id,
      review: googleReview.review_text,
      rating: googleReview.review_rating,
      backpacker: backpacker,
      reviewDate: googleReview.review_datetime_utc,
      createdAt: googleReview.review_datetime_utc,
      updatedAt: googleReview.review_datetime_utc,
    };
  }

  get hasBadges(): boolean {
    return this.hostel?.reviews?.some((review) => review.badges?.length > 0);
  }

  get roundedCombinedRating(): number {
    return Math.floor(this.combinedRating ?? 0); // Use Math.floor to round down
  }

  // Calculate combined rating from both hostel rating and Google rating
  get combinedRating(): number | null {
    const hostelRating = this.hostel?.overallRating || 0; // Get hostel rating, default to 0 if not present
    const googleRating = this.hostel?.google_review_data
      ? this.hostel?.google_review_data.rating || 0
      : 0; // Get Google rating, default to 0 if not present

    // Count how many ratings are available (1 for each non-zero rating)
    const ratingCount = (hostelRating > 0 ? 1 : 0) + (googleRating > 0 ? 1 : 0);

    // If there are no ratings, return null
    if (ratingCount === 0) return null;

    // Calculate the combined rating by averaging both ratings
    return (hostelRating + googleRating) / ratingCount;
  }

  // Calculate the total combined review count (hostel reviews + Google reviews)
  get combinedReviewCount(): number {
    const hostelReviews = this.hostel?.totalReviews || 0; // Hostel's own review count
    const googleReviews = this.hostel?.google_review_data?.reviews_count || 0; // Google review count

    return hostelReviews + googleReviews;
  }

  // Determine the rating description based on combined rating
  get ratingDescription(): string {
    const rating = this.combinedRating;
    if (rating >= 8) return 'Awesome';
    if (rating >= 6) return 'Great';
    if (rating >= 4) return 'Good';
    return 'Average';
  }

  ngOnDestroy() {
    this.destroyed.next();
    this.destroyed.complete();
  }
}
